import { Box, Typography, Paper } from '@mui/material';
import { DataTable, type DataTableColumn } from '@/components/ui';

interface MonthRow {
  month: string;
  income: number;
  expense: number;
  netIncome: number;
}

interface MonthlySummaryTableProps {
  monthlyData: Array<{ month: number; income: number; expense: number; netIncome: number }>;
  selectedYear: number;
}

const fmt = (n: number): string => (n !== 0 ? n.toLocaleString('ko-KR') : '-');

const MonthlySummaryTable: React.FC<MonthlySummaryTableProps> = ({ monthlyData, selectedYear }) => {
  const rows: MonthRow[] = monthlyData.map((m) => ({
    month: `${m.month}월`,
    income: m.income,
    expense: m.expense,
    netIncome: m.netIncome,
  }));

  const totals = monthlyData.reduce(
    (a, m) => ({ income: a.income + m.income, expense: a.expense + m.expense, netIncome: a.netIncome + m.netIncome }),
    { income: 0, expense: 0, netIncome: 0 },
  );
  rows.push({ month: '합계', income: totals.income, expense: totals.expense, netIncome: totals.netIncome });

  const numSx = (color: string) => ({ className: 'num', style: { color, fontVariantNumeric: 'tabular-nums' as const } });

  const columns: DataTableColumn<MonthRow>[] = [
    { key: 'month', label: '월', render: (r) => <span style={{ fontWeight: r.month === '합계' ? 700 : 500 }}>{r.month}</span> },
    { key: 'income', label: '매출', align: 'right', render: (r) => <span {...numSx('var(--c-green-700)')}>{fmt(r.income)}</span> },
    { key: 'expense', label: '지출', align: 'right', render: (r) => <span {...numSx('var(--c-red-600)')}>{fmt(r.expense)}</span> },
    { key: 'netIncome', label: '영업이익', align: 'right',
      render: (r) => <span {...numSx(r.netIncome < 0 ? 'var(--c-red-600)' : r.netIncome > 0 ? 'var(--c-green-800)' : 'var(--fg-strong)')}>{fmt(r.netIncome)}</span> },
  ];

  return (
    <Paper elevation={0} sx={{
      p: { xs: 2, sm: 3 }, background: 'var(--bg-base)',
      borderRadius: 'var(--r-16)', border: '1px solid var(--border-subtle)',
    }}>
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 'var(--size-md)', fontWeight: 700, color: 'var(--fg-strong)' }}>
          {selectedYear}년 월별 손익 현황
        </Typography>
        <Typography sx={{ fontSize: 'var(--size-sm)', color: 'var(--fg-tertiary)' }}>
          월별 매출·지출·영업이익 요약
        </Typography>
      </Box>
      <DataTable columns={columns} rows={rows} rowKey={(r) => r.month} emptyTitle="표시할 월별 데이터가 없어요" />
    </Paper>
  );
};

export default MonthlySummaryTable;

